import { fromJS, Map, List } from 'immutable';
import _ from 'lodash';

import { businessDetailsFormValidation } from '@src/containers/BusinessIntroduction/businessDetailsSchema';

import UKValidationSchema from './applicationValidationSchema';
import DEValidationSchema from './applicationValidationSchemaDE';
import {
    dateFormatter,
    dateParser,
    removeEmptyStrings,
    getSchemaSpecificCompanyType
} from '../../src/helpers/helpers';
import validateBusinessIntroductionUK from '../containers/BusinessIntroduction/validate';
import * as personalInformationSchemasUK from '../containers/PersonalInformation/schema';
import validatePersonalInformationUK from '../containers/PersonalInformation/validate';

const UK = 'uk';

export function buildApplicationState(fetchedValues = {}, formValues = {}) {
    formValues = fromJS(formValues).reduce((result, item) => {
        if (item.get('people')) {
            const concat = result.get('people')
                ? result.get('people').concat(item.get('people'))
                : item.get('people');
            return result.set('people', concat);
        }
        return result.mergeDeep(item);
    }, Map());

    fetchedValues =
        fromJS(fetchedValues).get('company') &&
        formValues.get('company') &&
        formValues.getIn(['company', 'providers'])
            ? fromJS(fetchedValues).set(
                  'company',
                  fromJS(fetchedValues).get('company').delete('providers')
              )
            : fromJS(fetchedValues);

    let people = fetchedValues.get('people');
    if (formValues.get('people')) {
        people = formValues.get('people').concat(fetchedValues.get('people'));
    }
    let state = fetchedValues.mergeDeep(formValues);
    state = state.set('people', people);
    if (state.get('people')) {
        // TODO: make it with immutableJS
        let people = _.uniqBy(
            state.get('people').toJS(),
            (person) => person.uid
        ).filter((person) => person.uid);
        state = state.set('people', fromJS(people));
    }
    return state.toJS();
}

function _personMapper(cb, person) {
    if (person.get('date_of_birth')) {
        return person.merge(
            fromJS({
                date_of_birth: cb(
                    Map.isMap(person.get('date_of_birth'))
                        ? person.get('date_of_birth').toJS()
                        : person.get('date_of_birth')
                )
            })
        );
    }
    return person;
}

export function checkForMissingAnswers(
    state = {},
    extraProps = {},
    locale = UK
) {
    const schemaValidation = (state, props) => {
        const {
            company: { type }
        } = state;
        const mappedType = getSchemaSpecificCompanyType(type);
        const schemas = {
            [UK]: {
                businessIntroductionSchema: businessDetailsFormValidation({
                    soleTrader: mappedType === 'st'
                }),
                personalInformationSchema:
                    // eslint-disable-next-line import/namespace
                    personalInformationSchemasUK[mappedType]
            }
        };
        const validators = {
            [UK]: {
                businessIntroductionValidator: validateBusinessIntroductionUK,
                personalInformationValidator: validatePersonalInformationUK
            }
        };

        const schemaStepValidators = validators[locale];
        return new Promise((res, rej) => {
            return schemaStepValidators
                .businessIntroductionValidator(
                    schemas[locale].businessIntroductionSchema
                )(state, null, props)
                .catch(() => {
                    rej({ step_id: 'businessIntroduction' });
                })
                .then(() => {
                    return schemaStepValidators.personalInformationValidator(
                        schemas[locale].personalInformationSchema
                    )(state, null, props);
                })
                .catch(() => {
                    rej({ step_id: 'personalInformation' });
                })
                .then(() => res(state));
        });
    };

    return serializeApplicationState(state)
        .then((state) => deserializeApplicationState(state))
        .then((state) => schemaValidation(state, extraProps));
}

export function serializeApplicationState(state = {}, schema = UK) {
    state = fromJS(state);
    const schemas = {
        uk: UKValidationSchema,
        de: DEValidationSchema
    };

    const validationSchema = schemas[schema];

    return new Promise((res) => {
        const company = state.get('company');
        const registered = company && company.get('registered');
        const people = state.getIn(['people']);
        if (registered) {
            state = state.merge(
                fromJS({
                    company: company.set(
                        'registered',
                        dateFormatter(registered.toJS())
                    )
                })
            );
        }

        if (people) {
            state = state.merge(
                fromJS({
                    people: people.map(
                        _personMapper.bind(null, dateFormatter),
                        List()
                    )
                })
            );
        }
        res(state);
    })
        .then((state) => {
            return validationSchema.validate(state.toJS(), {
                abortEarly: false
            });
        })
        .catch(function (error) {
            if (error.inner) {
                // TODO: remove sorting make a validation recursive?
                return error.inner
                    .map((error) => error.path)
                    .sort((a, b) => a > b)
                    .reverse();
            }
            return [];
        })
        .then((paths) => {
            paths.forEach((path) => {
                path = path
                    .replace(/[\[\]']+/g, '.')
                    .split('.')
                    .filter((s) => s);
                state = state.deleteIn(path);
            });
            if (schema !== UK) {
                /**
                 * Remove keys with empty values from the state
                 * => PL ONLY
                 */
                return removeEmptyStrings(state.toJS());
            }
            return state.toJS();
        });
}

export function deserializeApplicationState(state = {}) {
    state = fromJS(state);
    const company = state.get('company');
    const registered = company && company.get('registered');
    const people = state.getIn(['people']);

    if (registered) {
        state = state.merge(
            fromJS({
                company: company.set('registered', dateParser(registered))
            })
        );
    }

    if (people) {
        state = state.merge(
            fromJS({
                people: people.map(_personMapper.bind(null, dateParser), List())
            })
        );
    }
    return state.toJS();
}
